<template>
  <main class="admin-page">
    <div class="admin-shell">
      <MarketingToolHeader
        eyebrow="Leadership Academy"
        title="Leadership verwalten"
        description="Pflege Coaching-Inhalte, prüfe Video-Evaluationen und organisiere die 1:1-Sessions zentral."
        icon="mdi-shield-account-outline">
        <template v-slot:actions>
          <v-btn icon class="icon-action" aria-label="Zurück zu Leadership" title="Zurück zu Leadership" to="/akademie/leadership"><v-icon>mdi-arrow-left</v-icon></v-btn>
          <span class="admin-badge"><v-icon>mdi-lock-outline</v-icon>Leadership Admin</span>
        </template>
      </MarketingToolHeader>

      <nav class="admin-nav" aria-label="Leadership Verwaltungsbereiche">
        <button
          v-for="(item, index) in navItems"
          :key="item.label"
          type="button"
          :class="{ active: adminTab === index }"
          :aria-current="adminTab === index ? 'page' : null"
          @click="selectTab(index)">
          <span><v-icon>{{ item.icon }}</v-icon></span>
          <div><strong>{{ item.label }}</strong><small>{{ item.description }}</small></div>
          <v-icon class="nav-arrow">mdi-chevron-right</v-icon>
        </button>
      </nav>

      <v-tabs-items v-model="adminTab" dark class="admin-content">
        <v-tab-item><section class="admin-surface"><ModuleManager track="leadership" heading="Leadership-Module" /></section></v-tab-item>
        <v-tab-item><section class="admin-surface"><LeadershipReviewManager /></section></v-tab-item>
        <v-tab-item><section class="admin-surface"><LeadershipCoachingAdmin /></section></v-tab-item>
      </v-tabs-items>
    </div>
  </main>
</template>

<script>
import LeadershipCoachingAdmin from "./LeadershipCoachingAdmin.vue"
import LeadershipReviewManager from "./LeadershipReviewManager.vue"
import ModuleManager from "./ModuleManager.vue"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  name: "LeadershipAdmin",
  components: { LeadershipCoachingAdmin, LeadershipReviewManager, ModuleManager, MarketingToolHeader },
  data() {
    return {
      adminTab: 0,
      navItems: [
        { label: "Coaching-Inhalte", description: "Tools, Videos & Evaluationen", icon: "mdi-bookshelf" },
        { label: "Video-Prüfung", description: "Abgaben & Feedback", icon: "mdi-video-check-outline" },
        { label: "Coaching 1:1", description: "Zeiten, Buchungen & Anfragen", icon: "mdi-account-clock-outline" },
      ],
    }
  },
  mounted() {
    this.syncTab()
  },
  watch: {
    "$route.query.tab"() {
      this.syncTab()
    },
  },
  methods: {
    syncTab() {
      const names = ["content", "reviews", "coaching"]
      const index = names.indexOf(this.$route.query.tab)
      if (index >= 0) this.adminTab = index
    },
    selectTab(index) {
      this.adminTab = index
      const names = ["content", "reviews", "coaching"]
      const query = { ...this.$route.query, tab: names[index] }
      if (names[index] !== "coaching") delete query.section
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.admin-page {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(139, 92, 246, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.admin-shell {
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #fff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.admin-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.8125rem;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
  font-size: 0.875rem;
  font-weight: 750;
}

.admin-badge .v-icon {
  color: currentColor;
}

.admin-nav {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.625rem;
  margin: 1rem 0;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.026);
}

.admin-nav button {
  display: grid;
  min-height: 4.5rem;
  grid-template-columns: 2.625rem minmax(0, 1fr) 1.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.9375rem;
  outline: none;
  color: #d4d4d8;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.admin-nav button.active {
  border-color: rgba(139, 92, 246, 0.32);
  color: #fff;
  background: linear-gradient(120deg, rgba(139, 92, 246, 0.14), rgba(255, 255, 255, 0.035));
}

.admin-nav button:focus-visible {
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
}

.admin-nav button > span {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.055);
}

.admin-nav button.active > span {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.admin-nav button > span .v-icon {
  color: currentColor;
}

.admin-nav button > div {
  display: grid;
  min-width: 0;
}

.admin-nav strong {
  font-size: 0.875rem;
  line-height: 1.35;
}

.admin-nav small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.nav-arrow {
  color: #8f9095 !important;
}

.admin-content {
  overflow: visible;
  background: transparent !important;
}

.admin-surface {
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.03);
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.admin-surface ::v-deep .gold-text {
  color: #7ee5a6 !important;
}

@media (max-width: 68rem) {
  .admin-nav {
    display: flex;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
  }

  .admin-nav button {
    flex: 1 0 15rem;
    scroll-snap-align: start;
  }
}

@media (max-width: 47.99rem) {
  .admin-page {
    padding: 1rem;
  }

  .admin-surface {
    padding: 0.875rem;
    border-radius: 1.0625rem;
  }
}

@media (max-width: 37.49rem) {
  .admin-page {
    padding: 0.75rem;
  }

  .admin-nav {
    margin: 0.75rem 0;
    padding: 0.375rem;
    border-radius: 1.0625rem;
  }

  .admin-nav button {
    flex-basis: 13.5rem;
  }

  .admin-surface {
    padding: 0.75rem;
  }
}
</style>
